import {
  Enabled,
  StaleTimeFunction,
  UseQueryOptions,
  useQuery,
} from '@tanstack/react-query';
import { useEffect } from 'react';

import { HttpStatusCode, PejHttpError } from '@common-lib';

import { useRoot } from '@frontend/utils';

export interface UseQueryClientProps<T> {
  /**
   * Fetches the data. The `signal` aborts when TanStack Query cancels the
   * fetch (e.g. an invalidation while a request is in flight); pass it on to
   * the client call so the HTTP request is dropped too.
   */
  onFetch: (context: { signal: AbortSignal }) => Promise<T>;
  onError?: (error: Error) => void;
  enabled?: Enabled<T, Error, T, string[]> | undefined;

  /**
   * The time in milliseconds after data is considered stale.
   * If set to `Infinity`, the data will never be considered stale.
   * If set to a function, the function will be executed with the query to compute a `staleTime`.
   * Defaults to `0`.
   */
  staleTime?: StaleTimeFunction<T, Error, T, string[]>;

  /**
   * Number of times to retry a failed query, or a function that receives the
   * failure count and error and returns whether to retry.
   * Defaults to TanStack Query's built-in default (3).
   */
  retry?: boolean | number | ((failureCount: number, error: Error) => boolean);

  /**
   * Delay in milliseconds between retries, or a function that receives the
   * attempt index and the error, and returns the delay.
   * Defaults to TanStack Query's built-in exponential backoff.
   */
  retryDelay?: number | ((attemptIndex: number, error: Error) => number);

  queryKey: string[];

  /**
   * Refetch on this interval in milliseconds, or a function of the query
   * returning the interval. `false` or `undefined` disables it. Interval
   * refetches are skipped while a fetch is already in flight and while the
   * app is not focused.
   */
  refetchInterval?: UseQueryOptions<T, Error, T, string[]>['refetchInterval'];

  /**
   * Optional placeholder value shown while the first fetch for this query
   * key is in flight. Useful for seeding a view from data already
   * available in another cached query (e.g. a list) to avoid flashing
   * stale or empty data before the fresh response arrives.
   */
  placeholderData?: () => T | undefined;
}

export const useClientQuery = <T>({
  onFetch: fetch,
  onError,
  enabled,
  staleTime,
  retry,
  retryDelay,
  queryKey,
  refetchInterval,
  placeholderData,
}: UseQueryClientProps<T>) => {
  const { removeLocalSession } = useRoot();

  const state = useQuery({
    queryKey: queryKey,
    staleTime: staleTime,
    // Pass the context through untouched. TanStack marks the abort signal as
    // consumed as soon as `signal` is read, and a consumed signal makes the
    // query drop its result when the last observer unmounts mid-fetch. Only
    // callers that actually read `signal` should opt into that.
    queryFn: (context) => fetch(context),
    enabled,
    retry,
    retryDelay,
    refetchInterval,
    placeholderData: placeholderData as UseQueryOptions<
      T,
      Error,
      T,
      string[]
    >['placeholderData'],
  });

  useEffect(() => {
    if (state.isError) {
      if (state.error instanceof PejHttpError) {
        if (
          state.error.statusCode === HttpStatusCode.UNAUTHORIZED ||
          state.error.statusCode === HttpStatusCode.LOCKED
        ) {
          removeLocalSession();
        }
      }

      if (state.error instanceof Error) {
        onError?.(state.error);
      }
    }
  }, [state.isError, state.error]);

  return state;
};
